import React, { useState } from 'react';
import { Button, Input, Form, useToast } from '@crisellevaldez/speculo-ui';
import { CheckCircle2 } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { ConfirmationDialog } from '../../../common/dialogs/ConfirmationDialog';
import { useClientOwner } from '../../../../hooks/useClientOwner';
import { getClientOwner } from '../../../../constants/clientOwners';
import {
  isOnboardingMarkedComplete,
  OnboardingCompleteData,
  resolveSupportContact,
  SUPPORT_CONTACT,
} from '../../../../constants/supportContact';
import { DataField } from './DataField';
import { FieldGrid, SectionCard } from './SectionComponents';

interface CrashCourseData {
  meeting_link?: string;
  completed?: boolean;
  auto_populated_at?: string;
  completed_at?: string;
}

interface OnboardingDataShape {
  crash_course?: CrashCourseData;
  crashCourse?: CrashCourseData;
  onboarding_complete?: OnboardingCompleteData;
  [key: string]: unknown;
}

interface StrategyCallSectionProps {
  selectedOrg: Organization;
  onboardingSetup: unknown;
  onboardingData: OnboardingDataShape;
  asTab?: boolean;
  onRefresh?: () => void;
}

type ConfirmTarget = 'onboarding' | 'strategyCall';

interface StatusRowProps {
  title: string;
  description: string;
  checked: boolean;
}

const StatusRow: React.FC<StatusRowProps> = ({ title, description, checked }) => (
  <div className="flex items-start gap-3">
    <CheckCircle2
      className={`w-5 h-5 mt-0.5 shrink-0 ${checked ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}
    />
    <div>
      <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{title}</p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  </div>
);

interface StatusCheckboxProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const StatusCheckbox: React.FC<StatusCheckboxProps> = ({ label, checked, disabled, onToggle }) => (
  <label className="flex items-center gap-2 cursor-pointer shrink-0">
    <input
      type="checkbox"
      checked={checked}
      onChange={onToggle}
      disabled={disabled}
      className="w-4 h-4 text-yellow-600 border-gray-300 dark:border-white/15 rounded focus:ring-yellow-500 cursor-pointer"
    />
    <span className="text-sm font-normal text-gray-700 dark:text-gray-300">{label}</span>
  </label>
);

// Keep the export name for backwards compatibility but internally use Strategy Call naming
export const CrashCourseSection: React.FC<StrategyCallSectionProps> = ({
  selectedOrg,
  onboardingData,
  onRefresh,
}) => {
  const { showToast } = useToast();
  const { updateSettings, isPending } = useUpdateOrganizationSettings();
  const owner = useClientOwner();
  // The org being edited, not the admin's own active org.
  const selectedOrgOwner = getClientOwner(selectedOrg?.client_owner);

  // Canonical shape: snake_case. Accept legacy camelCase `crashCourse` from
  // old Firestore onboarding so legacy orgs see correct status.
  const strategyCallData = onboardingData?.crash_course ?? onboardingData?.crashCourse;
  const strategyMeetingLink = strategyCallData?.meeting_link;
  const strategyAutoPopulatedAt = strategyCallData?.auto_populated_at;
  // Default the input to the current client owner's URL on every open so
  // reassigning Ben → Leslie auto-refreshes the admin view, even if the org
  // still has the previous owner's URL persisted in onboardingData. The admin
  // can still overwrite the value before saving if they need a custom link.
  const [meetingLink, setMeetingLink] = useState(owner.strategyMeetingUrl);
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  // Optimistic local state — updated immediately after save so UI reflects changes
  // without waiting for GQL refetch (GQL may lag behind Remi REST PUT)
  const [localCompleted, setLocalCompleted] = useState<boolean | null>(null);
  const [localScheduledLink, setLocalScheduledLink] = useState<string | null>(null);
  const [localOnboardingComplete, setLocalOnboardingComplete] =
    useState<OnboardingCompleteData | null>(null);

  const savedLink = localScheduledLink ?? strategyMeetingLink;
  const isCompleted = localCompleted !== null ? localCompleted : strategyCallData?.completed === true;
  const isScheduled = Boolean(savedLink);
  const isAutoPopulated = Boolean(strategyAutoPopulatedAt);
  const onboardingComplete = localOnboardingComplete ?? onboardingData?.onboarding_complete;
  const isMarkedComplete = isOnboardingMarkedComplete(onboardingComplete);
  const supportContact = resolveSupportContact(onboardingComplete, selectedOrgOwner);

  // Both checkboxes write the same onboardingData JSON, so each save carries
  // the other's optimistic value; otherwise a stale prop would revert it.
  const baseOnboardingData: OnboardingDataShape = {
    ...(onboardingData ?? {}),
    ...((localScheduledLink !== null || localCompleted !== null) && {
      crash_course: { ...strategyCallData, meeting_link: savedLink ?? '', completed: isCompleted },
    }),
    ...(localOnboardingComplete !== null && { onboarding_complete: localOnboardingComplete }),
  };

  const handleSchedule = async () => {
    if (!selectedOrg?.id || !meetingLink.trim()) return;

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          onboardingData: {
            ...baseOnboardingData,
            crash_course: {
              meeting_link: meetingLink.trim(),
              completed: false,
            },
          },
        } as Partial<Organization>,
      });

      setLocalScheduledLink(meetingLink.trim());
      setLocalCompleted(false);
      showToast({
        message: 'Strategy call scheduled! Banner will now show to the client.',
        variant: 'success',
      });

      onRefresh?.();
    } catch (error) {
      console.error('Error scheduling strategy call:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to schedule strategy call',
        variant: 'error',
      });
    }
  };

  const handleCompletedToggle = () => {
    if (!isCompleted) {
      // Show confirmation dialog before marking as completed
      setConfirmTarget('strategyCall');
    } else {
      // Directly toggle off (no confirmation needed)
      handleConfirmCompleted(false);
    }
  };

  const handleConfirmCompleted = async (completed: boolean = true) => {
    if (!selectedOrg?.id) return;

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          onboardingData: {
            ...baseOnboardingData,
            crash_course: {
              meeting_link: strategyMeetingLink || '',
              completed,
              ...(completed && { completed_at: new Date().toISOString() }),
            },
          },
        } as Partial<Organization>,
      });

      setLocalCompleted(completed);
      showToast({
        message: completed
          ? 'Strategy call marked as complete! Banner will disappear.'
          : 'Strategy call marked as incomplete. Banner will show again.',
        variant: 'success',
      });

      onRefresh?.();
    } catch (error) {
      console.error('Error updating strategy call status:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update strategy call status',
        variant: 'error',
      });
    }
    setConfirmTarget(null);
  };

  const handleOnboardingCompleteToggle = () => {
    if (!isMarkedComplete) {
      setConfirmTarget('onboarding');
    } else {
      handleConfirmOnboardingComplete(false);
    }
  };

  const handleConfirmOnboardingComplete = async (completed: boolean = true) => {
    if (!selectedOrg?.id) return;
    const next: OnboardingCompleteData = {
      completed,
      ...(completed && { completed_at: new Date().toISOString() }),
    };

    try {
      await updateSettings({
        id: selectedOrg.id,
        data: {
          onboardingData: { ...baseOnboardingData, onboarding_complete: next },
        } as Partial<Organization>,
      });

      setLocalOnboardingComplete(next);
      showToast({
        message: completed
          ? `Onboarding marked as complete! Support contact is now ${selectedOrgOwner.displayName}.`
          : `Onboarding marked as incomplete. Support contact is back to ${SUPPORT_CONTACT.name}.`,
        variant: 'success',
      });

      onRefresh?.();
    } catch (error) {
      console.error('Error updating onboarding status:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update onboarding status',
        variant: 'error',
      });
    }
    setConfirmTarget(null);
  };

  return (
    <div className="space-y-4">
      {/* Onboarding Status */}
      <SectionCard
        title="Onboarding"
        action={
          <StatusCheckbox
            label="Mark Onboarding as Complete"
            checked={isMarkedComplete}
            disabled={isPending}
            onToggle={handleOnboardingCompleteToggle}
          />
        }
      >
        <StatusRow
          title="Onboarding Status"
          description={
            isMarkedComplete
              ? `Onboarding is complete. Support contact changed to the client owner, ${selectedOrgOwner.displayName}.`
              : `Marking onboarding as complete changes the support contact from ${SUPPORT_CONTACT.name} to the client owner, ${selectedOrgOwner.displayName}.`
          }
          checked={isMarkedComplete}
        />
        <div className="mt-4 pl-8">
          <FieldGrid>
            <DataField label="Support Contact" value={supportContact.name} />
            <DataField label="Support Email" value={supportContact.email} type="email" />
          </FieldGrid>
        </div>
      </SectionCard>

      {/* Strategy Call */}
      <SectionCard
        title="Strategy Call"
        action={
          isScheduled && (
            <StatusCheckbox
              label="Mark as Completed"
              checked={isCompleted}
              disabled={isPending}
              onToggle={handleCompletedToggle}
            />
          )
        }
      >
        {isScheduled ? (
          <>
            <StatusRow
              title="Strategy Call Status"
              description={
                isCompleted
                  ? 'Strategy call has been completed'
                  : isAutoPopulated
                    ? 'Strategy call banner auto-shown after onboarding - visible to client'
                    : 'Strategy call is scheduled - banner is visible to client'
              }
              checked={isCompleted}
            />
            {/* Follow the org's current client owner, not the (possibly stale)
                URL saved when the call was first scheduled, so reassigning
                Ben → Leslie auto-refreshes the admin view. */}
            <div className="mt-4 pl-8">
              <FieldGrid cols={1}>
                <DataField label="Meeting Link" value={owner.strategyMeetingUrl} type="link" />
              </FieldGrid>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter a meeting link to schedule a strategy call. A banner will show to the client
              prompting them to book a session.
            </p>
            <Form.Control>
              <Form.Label>Meeting Link</Form.Label>
              <Input
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://meetings.hubspot.com/..."
              />
            </Form.Control>
            <Button
              variant="primary"
              onClick={handleSchedule}
              isLoading={isPending}
              disabled={!meetingLink.trim()}
            >
              Schedule Strategy Call
            </Button>
          </div>
        )}
      </SectionCard>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={confirmTarget === 'onboarding'}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => handleConfirmOnboardingComplete(true)}
        title="Mark Onboarding as Complete"
        message={`Are you sure you want to mark onboarding as complete? This will change the client's support contact from ${SUPPORT_CONTACT.name} to their client owner, ${selectedOrgOwner.displayName}.`}
        confirmLabel="Mark as Complete"
        cancelLabel="Cancel"
        variant="info"
        isLoading={isPending}
      />
      <ConfirmationDialog
        isOpen={confirmTarget === 'strategyCall'}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => handleConfirmCompleted(true)}
        title="Mark Strategy Call as Completed"
        message="Are you sure you want to mark this strategy call as completed? This will hide the 'Schedule Your Strategy Call' banner for the client."
        confirmLabel="Mark as Completed"
        cancelLabel="Cancel"
        variant="info"
        isLoading={isPending}
      />
    </div>
  );
};
